import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IaHandoffBin } from '../../../types';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import type { FunilIaStageSlug } from '../funilIaStage';
import type { PhysicsBall } from '../engine/types';
import { useFunilIaData } from '../hooks/useFunilIaData';
import { useLeadBallSync } from '../hooks/useLeadBallSync';
import { usePhysicsEngine } from '../hooks/usePhysicsEngine';
import { LANE_CONFIGS } from '../engine/constants';
import { loadPhysicsTweaks } from '../physicsTweaks';
import { FunilIaStats } from './FunilIaStats';
import { HandoffBinsColumn } from './HandoffBinsColumn';
import { PhysicsLane } from './PhysicsLane';
import { PhysicsTweaksPanel } from './PhysicsTweaksPanel';
import { TrailLayer } from './TrailLayer';
import './FunilIaPhysics.css';

export interface FunilIaPhysicsProps {
  onOpenLead?: (leadId: string) => void;
}

export const FunilIaPhysics: React.FC<FunilIaPhysicsProps> = ({ onOpenLead }) => {
  const { organizationId, organization } = useAuth();
  const {
    campaigns,
    grouped,
    loading,
    error,
    persistHandoff,
    swatchForCampaign,
    allFunnelLeads,
  } = useFunilIaData();

  const [handoffBinsUi, setHandoffBinsUi] = useState(() =>
    Boolean(organization?.aiSettings?.handoffBinsUi)
  );
  useEffect(() => {
    if (!organizationId) return;
    void apiService.getAISettings(organizationId).then((s) => setHandoffBinsUi(!!s.handoffBinsUi));
  }, [organizationId, organization?.aiSettings?.handoffBinsUi]);

  const rBinHuman = useRef<HTMLDivElement>(null);
  const rBinInactive = useRef<HTMLDivElement>(null);
  const rBinLow = useRef<HTMLDivElement>(null);
  const rBinOptout = useRef<HTMLDivElement>(null);
  const rBinToxic = useRef<HTMLDivElement>(null);
  const handoffBinRefs = useMemo<Record<IaHandoffBin, React.RefObject<HTMLDivElement | null>>>(
    () => ({
      human: rBinHuman,
      inactive: rBinInactive,
      lowintent: rBinLow,
      optout: rBinOptout,
      toxic: rBinToxic,
    }),
    []
  );

  const laneRefs: Record<FunilIaStageSlug, React.RefObject<HTMLDivElement | null>> = {
    inicio: useRef<HTMLDivElement>(null),
    em_negociacao_ia: useRef<HTMLDivElement>(null),
    proposta_ia: useRef<HTMLDivElement>(null),
  };

  const humanRefs: Record<FunilIaStageSlug, React.RefObject<HTMLDivElement | null>> = {
    inicio: useRef<HTMLDivElement>(null),
    em_negociacao_ia: useRef<HTMLDivElement>(null),
    proposta_ia: useRef<HTMLDivElement>(null),
  };

  const onHandoffComplete = useCallback((leadId: string) => {
    void persistHandoff(leadId);
  }, [persistHandoff]);

  const tweaksRef = useRef(loadPhysicsTweaks());
  const swatchResolverRef = useRef(swatchForCampaign);
  swatchResolverRef.current = swatchForCampaign;
  const useCampaignBallSwatchRef = useRef(false);

  const { balls, trails, enqueueCommand, registerBallElement } = usePhysicsEngine({
    laneRefs,
    humanRefs,
    onHandoffComplete,
    tweaksRef,
    swatchResolverRef,
    useCampaignBallSwatchRef,
    handoffBinsUi,
    binRefs: handoffBinsUi ? handoffBinRefs : undefined,
  });

  // Campaign names for ball initial letter
  const campaignNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of campaigns) {
      map[c.campaignId] = c.nome || '';
    }
    return map;
  }, [campaigns]);

  // Sync leads → physics commands
  useLeadBallSync({
    grouped,
    allFunnelLeads,
    balls,
    campaignNames,
    onCommand: enqueueCommand,
    onHandoffComplete,
  });

  /** Não usar useMemo com [balls]: `balls` é sempre o mesmo Map (ref), ficava congelado no 1.º render. */
  const ballsByLane: Record<FunilIaStageSlug, PhysicsBall[]> = {
    inicio: [],
    em_negociacao_ia: [],
    proposta_ia: [],
  };
  balls.forEach((ball) => {
    ballsByLane[ball.lane]?.push(ball);
  });

  const processingCount = useMemo(() => {
    return Object.values(grouped).reduce((acc, leads) => acc + leads.length, 0);
  }, [grouped]);

  if (loading) {
    return (
      <div className="funil-ia-physics">
        <p style={{ color: 'var(--ink-400)' }}>A carregar campanhas com funil IA…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="funil-ia-physics">
        <p style={{ color: '#BE123C' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="funil-ia-physics">
      <header className="funil-ia-header">
        <div className="funil-ia-header-left">
          <h2>Funil IA</h2>
          {campaigns.length === 0 ? (
            <p className="funil-ia-header-sub">
              Nenhuma campanha ativa com modo «Funil IA». Defina o tipo de atendimento na campanha e ative a
              campanha.
            </p>
          ) : (
            <p className="funil-ia-header-sub">
              Acompanhe os leads sendo processados automaticamente pela inteligência artificial
            </p>
          )}
          {campaigns.length > 0 && (
            <ul className="funil-ia-campaign-names" aria-label="Campanhas do funil">
              {campaigns.map((c) => (
                <li key={c.campaignId} className="funil-ia-campaign-name-pill">
                  {c.nome?.trim() || c.campaignId}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="funil-ia-header-aside">
          {campaigns.length > 0 && (
            <div className="funil-ia-header-status">
              <span className="funil-ia-status-pill">
                <span className="funil-ia-status-pill-dot" aria-hidden />
                IA ativa
              </span>
              <span className="funil-ia-header-lead-count">
                {processingCount} lead{processingCount === 1 ? '' : 's'} em processamento
              </span>
            </div>
          )}
        </div>
      </header>

      <FunilIaStats grouped={grouped} />

      <div className={`funil-ia-funnel${handoffBinsUi ? ' funil-ia-funnel--handoff-bins' : ''}`}>
        <div className="funil-ia-funnel-row">
          <div className="funil-ia-lanes">
            {LANE_CONFIGS.map((config) => (
              <PhysicsLane
                key={config.key}
                config={config}
                balls={ballsByLane[config.key]}
                activeCount={grouped[config.key].length}
                laneRef={laneRefs[config.key]}
                humanRef={humanRefs[config.key]}
                useCampaignSwatch={false}
                swatchForCampaign={swatchForCampaign}
                registerBall={registerBallElement}
                onBallClick={onOpenLead}
                hideLaneHuman={handoffBinsUi}
              />
            ))}
          </div>
          {handoffBinsUi && (
            <HandoffBinsColumn
              leads={allFunnelLeads}
              binRefs={handoffBinRefs}
            />
          )}
        </div>
        <TrailLayer laneRefs={laneRefs} trails={trails} />
      </div>
      <PhysicsTweaksPanel tweaksRef={tweaksRef} />
    </div>
  );
};
